import {getPatientFullName} from '@/modules/patients/functions'
import {getPreparationExpiry, selectUseByStabilityRule} from '@cypro/engine/utils'
import {computeActualDoses, getWeighedMedicineIds} from './actual-dose'
import {getDataMatrixPayload} from './data-matrix'
import {
  assertDoseUnits,
  createLabelFormat,
  formatBatches,
  formatLabelDate,
  formatLabelDateTime,
  LABEL_SEPARATOR,
  MAX_VIAL_BATCHES,
  splitProductionNumber,
} from './format'
import {type CompactLabelContent, estimateTextWidth, fitCompactLabel, SMALL_TEXT_PX} from './label-fit'
import {type LabelGeometry, LABEL_SIZES} from './label-sizes'
import {buildSubstanceHeadings} from './substance-headings'
import type {LabelTranslate, PreparationLabelData, PreparationLabelInput} from './types'
import {isDefined} from '@cypro/ui/utils'

const FIELD_GAP = '    '
const NO_VALUE = '—'
// a medicine counts as fully weighed when the weighed allocations cover its prescribed dose
const COVERAGE_TOLERANCE = 0.005

/** actual dose per medicine, only where the weighed allocations cover the dose still to add */
export function getReliableActualDoses(input: PreparationLabelInput) {
  const actualDoses = computeActualDoses(input.weighings)
  const reliable = new Map<string, number>()
  for (const substance of input.substances) {
    const actual = actualDoses.get(substance.medicineId)
    if (!actual) continue
    // a returned preparation already provides part of the dose; the bag carries both
    const providedByReturn = substance.providedByReturn ?? 0
    if (
      Math.abs(actual.coveredDose + providedByReturn - substance.prescribedDose) >
      substance.prescribedDose * COVERAGE_TOLERANCE
    )
      continue
    reliable.set(substance.medicineId, actual.actualDose + providedByReturn)
  }
  return reliable
}

/** dose per ml by the organisation's concentration basis; with several substances a single figure would mislead */
export function getConcentrationPerMl(input: PreparationLabelInput) {
  const {settings, substances, carrier} = input
  const volume = settings.concentrationBasis === 'TOTAL_VOLUME' ? input.totalVolumeMl : (carrier?.volumeMl ?? null)
  if (!settings.concentration || substances.length !== 1 || !volume) return null
  return substances[0].prescribedDose / volume
}

/** the name in the order it prints, split where it may wrap: "Last," and "First", or "First" and "Last" */
function getPatientNameParts(
  patient: PreparationLabelInput['patient'],
  order: PreparationLabelInput['settings']['patientNameOrder']
) {
  const parts =
    order === 'LAST_NAME_FIRST'
      ? [patient.familyName && patient.givenName ? `${patient.familyName},` : patient.familyName, patient.givenName]
      : [patient.givenName, patient.familyName]
  return parts.filter((part): part is string => Boolean(part))
}

/** the most vial batches whose line fits one compact line, then "+n" (CY-421) */
function fitBatchesLine(input: PreparationLabelInput, t: LabelTranslate, geometry: LabelGeometry) {
  for (let count = MAX_VIAL_BATCHES; count > 1; count--) {
    const line = formatBatches(input.vialBatchLots, input.carrierBatchLots, t, count)
    if (!line || estimateTextWidth(line, SMALL_TEXT_PX) <= geometry.bodyWidth) return line
  }
  return formatBatches(input.vialBatchLots, input.carrierBatchLots, t, 1)
}

export function buildPreparationLabelData(input: PreparationLabelInput, t: LabelTranslate): PreparationLabelData {
  const {settings, patient, encounter, substances, carrier} = input
  assertDoseUnits(substances, input.productionNumber)
  const format = createLabelFormat(settings.language)
  const dataMatrixPayload = getDataMatrixPayload(input.productionNumber, patient)

  const showCustomer = settings.customerName && Boolean(encounter?.customerLabelName)

  const patientName = getPatientFullName(patient, settings.patientNameOrder)

  const detailsParts = [
    isDefined(patient.birthDate) ? `${t('preparation_label.dob')}: ${formatLabelDate(patient.birthDate)}` : null,
    // a patient registered to a customer is identified by the customer, not the hospital number
    settings.hospitalNumber && patient.hospitalPatientId && !showCustomer
      ? `${t('preparation_label.hospital_no')}: ${patient.hospitalPatientId}`
      : null,
  ]

  const wardText = encounter?.wardName ? `${t('preparation_label.ward')}: ${encounter.wardName}` : null
  const locationPart = showCustomer
    ? [encounter?.customerLabelName, settings.wardNextToCustomerName ? wardText : null]
        .filter(Boolean)
        .join(LABEL_SEPARATOR)
    : wardText
  const doctorPart =
    settings.prescribingDoctor && encounter?.doctorName
      ? `${t('preparation_label.doctor')}: ${encounter.doctorName}`
      : null
  const wardLine = [locationPart, doctorPart].filter(Boolean).join(FIELD_GAP) || null
  const geometry = LABEL_SIZES[settings.labelSize]

  const {headings: substanceHeadings, size: substanceHeadingSize} = buildSubstanceHeadings(
    substances,
    format,
    geometry.headingOneLineMax
  )

  // "prescription trade name · manufacturer" under the "substance dose" heading
  const productParts = substances
    .map(s =>
      [s.tradeName, settings.manufacturer ? s.manufacturer : null].filter((part): part is string => Boolean(part))
    )
    .filter(parts => parts.length > 0)
  const productLines = productParts.map(parts => parts.join(LABEL_SEPARATOR))

  const carrierPart = carrier
    ? [
        carrier.name,
        carrier.volumeMl ? `${format.dose(carrier.volumeMl)} ml` : null,
        carrier.volumeMode ? `(${carrier.volumeMode.toLowerCase()})` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : null
  const carrierParts = [carrierPart, input.routeName, input.durationMinutes ? `${input.durationMinutes} min` : null]
  const carrierLine = carrierParts.filter(Boolean).join(LABEL_SEPARATOR) || null

  const volumePart =
    settings.labelVolume === 'TOTAL_VOLUME' && input.totalVolumeMl
      ? `${t('preparation_label.total_volume')}: ${format.volume(input.totalVolumeMl)} ml`
      : settings.labelVolume === 'SUBSTANCE_VOLUME' && input.substanceVolumeMl
        ? `${t('preparation_label.substance_volume')}: ${format.volume(input.substanceVolumeMl)} ml`
        : null
  const concentrationPerMl = getConcentrationPerMl(input)
  const concentrationPart =
    concentrationPerMl === null
      ? null
      : `${format.concentration(concentrationPerMl)} ${format.perMl(substances[0].doseUnit)}`
  const volumeLine = [volumePart, concentrationPart].filter(Boolean).join(LABEL_SEPARATOR) || null

  const reliableActualDoses = getReliableActualDoses(input)
  // a medicine weighed on the scale whose dose still cannot be worked out (a reading missing or the vials
  // not adding up) says so, instead of silently dropping the line (CY-137); the top-up workflow stays silent
  const weighedMedicineIds = getWeighedMedicineIds(input.weighings)
  const actualLines = settings.actualQuantity
    ? substances.flatMap(s => {
        const actualDose = reliableActualDoses.get(s.medicineId)
        if (actualDose === undefined) {
          return weighedMedicineIds.has(s.medicineId)
            ? [`${t('preparation_label.actual')}: ${t('preparation_label.not_measured')}`]
            : []
        }
        const difference = format.percentDifference(actualDose, s.prescribedDose)
        return [`${t('preparation_label.actual')}: ${format.oneDecimal(actualDose)} ${format.unit(s.doseUnit)} (${difference})`]
      })
    : []

  // the storage text and the light flag come from the very rule that sets the use-by, so a label never prints a
  // use-by without its storage (CY-410), e.g. a carrier-specific rule on a preparation without a carrier
  const rule = selectUseByStabilityRule({
    drugRules: input.stabilityRules,
    carrierRules: input.carrierStabilityRules,
    drugIsDrySubstance: input.drugIsDrySubstance,
    carrierIsDrySubstance: input.carrierIsDrySubstance,
  })
  // Use-by mirrors the engine's expiry: drug + carrier rules, capped by the batch expiries.
  const useByExpiry = input.producedAt
    ? getPreparationExpiry({
        referenceTime: input.producedAt,
        drugRules: input.stabilityRules,
        carrierRules: input.carrierStabilityRules,
        drugBatchExpiries: input.drugBatchExpiries,
        carrierBatchExpiries: input.carrierBatchExpiries,
        returnExpiries: input.returnExpiries,
        drugIsDrySubstance: input.drugIsDrySubstance,
        carrierIsDrySubstance: input.carrierIsDrySubstance,
      })
    : undefined
  const hazardNames = [...new Set(substances.flatMap(s => s.hazardClasses))]
  const flags = [
    hazardNames.length ? `${t('preparation_label.hazard')}: ${hazardNames.join(', ')}` : null,
    substances.some(s => s.containsAlcohol) ? t('preparation_label.contains_alcohol') : null,
    rule?.lightProtectionRequired ? t('preparation_label.protect_from_light') : null,
  ].filter((flag): flag is string => Boolean(flag))

  const bandSource = substances.some(s => s.cytotoxic) ? settings.bandTextCytotoxic : settings.bandTextNonCytotoxic
  const storage = rule?.storageLabelName || NO_VALUE
  const producedAt = input.producedAt ? formatLabelDateTime(input.producedAt) : null
  const preparedBy = settings.preparedBy ? input.preparedByInitials : null
  const batchesLine =
    geometry.layout === 'compact'
      ? fitBatchesLine(input, t, geometry)
      : formatBatches(input.vialBatchLots, input.carrierBatchLots, t)
  const pharmacyLine =
    settings.pharmacyNameAndAddress && input.pharmacy
      ? [input.pharmacy.name, input.pharmacy.address].filter(Boolean).join(LABEL_SEPARATOR)
      : null
  const linearBarcodePayload = settings.additionalLabelBarcode ? dataMatrixPayload : null

  // the 70 × 60 label prints each line's parts together when they fit, else one part per line (CY-421)
  const compactContent: CompactLabelContent | null =
    geometry.layout === 'compact'
      ? {
          patientName: getPatientNameParts(patient, settings.patientNameOrder),
          patientDetails: detailsParts.filter((part): part is string => Boolean(part)),
          ward: [locationPart, doctorPart].filter((part): part is string => Boolean(part)),
          headings: substanceHeadings.map(heading => `${heading.name} ${heading.dose}`),
          headingSize: substanceHeadingSize,
          products: productParts,
          carrier: carrierParts.filter((part): part is string => Boolean(part)),
          volume: [volumePart, concentrationPart].filter((part): part is string => Boolean(part)),
          actualLines,
          routeWarning: substances.some(s => s.routeWarning) ? t('preparation_label.route_warning') : null,
          // the storage condition is a chip after the flags; none when there is no storage text (CY-421)
          chips: storage === NO_VALUE ? flags : [...flags, storage],
          produced: [
            producedAt ? `${t('preparation_label.produced')}: ${producedAt}` : null,
            producedAt && preparedBy ? `${t('preparation_label.prepared_by')}: ${preparedBy}` : null,
          ].filter((part): part is string => Boolean(part)),
          hasBatches: Boolean(batchesLine),
          hasPharmacy: Boolean(pharmacyLine),
          hasLinearBarcode: Boolean(linearBarcodePayload),
        }
      : null

  return {
    size: settings.labelSize,
    bandText: bandSource.trim() || null,
    patientName,
    patientDetailsLine: detailsParts.filter(Boolean).join(FIELD_GAP),
    wardLine,
    substanceHeadings,
    substanceHeadingSize,
    productLines,
    productLinesFirst: settings.tradeNameBeforeSubstance,
    carrierLine,
    volumeLine,
    actualLines,
    administrationDate: formatLabelDate(input.administrationDate),
    routeWarning: substances.some(s => s.routeWarning),
    flags,
    useBy: useByExpiry ? formatLabelDateTime(useByExpiry) : NO_VALUE,
    storage,
    productionNumber: splitProductionNumber(input.productionNumber),
    producedAt,
    preparedBy,
    batchesLine,
    pharmacyLine,
    dataMatrixPayload,
    linearBarcodePayload,
    copies: Math.max(1, settings.labelCopies),
    fit: compactContent ? fitCompactLabel(compactContent, geometry, {field: FIELD_GAP, label: LABEL_SEPARATOR}) : null,
  }
}
